import React, { Component, ReactNode, ErrorInfo } from 'react';
import { useNotifications } from '@toolpad/core';

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback: (error: Error) => ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // You can log the error to an error reporting service here
        console.error("ErrorBoundary caught an error", error, errorInfo);
    }

    render() {
        if (this.state.hasError && this.state.error) {
            return this.props.fallback(this.state.error);
        }

        return this.props.children;
    }
}

const ErrorBoundaryWithNotifications: React.FC<{ children: ReactNode }> = ({ children }) => {
    const notifications = useNotifications();

    const fallback = (error: Error) => {
        notifications.show(`Error: ${error.message}`, {
            severity: 'error',
            autoHideDuration: 3000
        });
        return null;
    };

    return (
        <ErrorBoundary fallback={fallback}>
            {children}
        </ErrorBoundary>
    );
};

export default ErrorBoundaryWithNotifications;